import classNames from "classnames";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import withErrorHandler from "component/design-system/withErrorHandler";
import withRouteChangeComplete from "hoc/withRouteChangeComplete";
import { RootState } from "interfaces/generics.type";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import buildCategoryTreeUrl from "util/category/build-category-tree-url";
import {
  getCurrentCategoryPageType,
  isTier0Page, isTier1Page, isTier2Page, isTier3Page,
} from "util/helper/helper";
import { scrollTo } from "util/scroll-helper";
import { getTier0UrlAndPageType } from "../../util/category/get-tier0-url";
import FilterLine from "./FilterLine";
import stylesV2 from "./category-tree-v2.module.scss";
import CategoryTreeTier0 from "./category-tree/CategoryTreeTier0";
import RenderLink from "./filter/RenderLink";
import CategoryToggler from "./filter/product-filter/CategoryToggler";
import styles from "./filter/product-filter/product-filter.module.scss";

let isCategoryTreeClick = false;

function CategoryTree(props) {
  const {
    tier0s, appliedFilters, tiersWithUrls = [],
  } = props;
  const { urlInformation } = useSelector((state: RootState) => state.utils);

  if (!tier0s) {
    return null;
  }

  const router = useRouter();
  const { currentPageType: pageType } = useSelector((state: RootState) => state.utils);
  const { selectedVehicle } = useSelector((store: RootState) => store.garage);
  const {
    tier0 = "", tier1, tier2, tier3,
  } = urlInformation || {};
  const [categoryAgg, setCategoryAgg] = useState(tier0s);
  const { t } = useTranslation("category");
  const currentCategoryPageType = getCurrentCategoryPageType(urlInformation, router.query);

  const { url: tier0Url, pageType: tier0PageType } = getTier0UrlAndPageType(tiersWithUrls);

  useEffect(() => {
    setCategoryAgg(tier0s);
  }, [tier0s]);

  function setIsCategoryTreeClick() {
    // if (!customScroll) {
    //   return;
    // }
    isCategoryTreeClick = true;
  }

  function CategoryItem({
    item, category, t0Idx, activeCategory, activeParent,
  }) {
    const { href, asPath } = buildCategoryTreeUrl(
      category, categoryAgg, tier0Url, tier0PageType,
      t0Idx,
      router.query,
      item,
      pageType,
      tiersWithUrls,
      appliedFilters,
      selectedVehicle,
    );
    return (
      <div className={`d-flex ${styles.category_t3_item}`}>
        <FilterLine
          className={styles.left_line}
          left={18}
          width={10}
        />

        <i
          className={classNames("fa", styles.t3_arrow, {
            "fa-angle-right": true,
            // "fa-plus-square-o": hasNext,
          })}
        />
        <FilterLine
          className={styles.bottom_line}
          left={18}
          width={10}
        />
        <span
          className={classNames(styles.bucket_label, "p-l-5", "p-r-5", {
            [styles.active_parent]: activeParent && !activeCategory,
            [styles.active_tier]: activeCategory,
            [stylesV2.active_tier_v2]: activeCategory,
          })}
          data-testid={activeCategory ? "active-tier" : ""}
        >
          <RenderLink
            href={href}
            asPath={asPath}
            label={item.bucket}
            title={`Explore ${item.bucket} For Your Car`}
            className={classNames(styles.render_link, stylesV2.render_link_v2)}
            onClick={setIsCategoryTreeClick}
          />
        </span>
        <span className={styles.product_count}>
          (
          {item.count}
          )
        </span>
      </div>
    );
  }

  function getPath(item, category, t0Idx, removeFilterableAttributes) {
    return buildCategoryTreeUrl(
      category, categoryAgg, tier0Url, tier0PageType,
      t0Idx,
      router.query,
      item,
      pageType,
      tiersWithUrls,
      appliedFilters,
      selectedVehicle,
      removeFilterableAttributes,
    );
  }

  function isActiveCategory(pageTier: string, categoryName: string) {
    if (appliedFilters.tier3) {
      return isTier3Page(pageTier) && appliedFilters.tier3.toLowerCase() === categoryName.toLowerCase();
    } if (appliedFilters.tier2) {
      return isTier2Page(pageTier) && appliedFilters.tier2.toLowerCase() === categoryName.toLowerCase();
    } if (appliedFilters.tier1) {
      return isTier1Page(pageTier) && appliedFilters.tier1.toLowerCase() === categoryName.toLowerCase();
    } if (appliedFilters.tier0) {
      return isTier0Page(pageTier) && tier0 === categoryName.toLowerCase();
    }
    return false;
  }

  function setCategoryAggData(filterKey: string, categoryIndex, childCategories) {
    const leftCategoryAgg = categoryAgg.slice(0, categoryIndex.tier0);
    const rightCategoryAgg = categoryAgg.slice(categoryIndex.tier0 + 1);
    const tier1Agg = categoryAgg[categoryIndex.tier0].tier1s;
    switch (filterKey) {
    case "tier0":
      setCategoryAgg([
        ...leftCategoryAgg,
        { ...categoryAgg[categoryIndex.tier0], tier1s: childCategories },
        ...rightCategoryAgg,
      ]);
      break;
    case "tier1":
      setCategoryAgg([
        ...leftCategoryAgg,
        {
          ...categoryAgg[categoryIndex.tier0],
          tier1s: [
            ...tier1Agg.slice(0, categoryIndex.tier1),
            { ...tier1Agg[categoryIndex.tier1], tier2s: childCategories },
            ...tier1Agg.slice(categoryIndex.tier1 + 1),
          ],
        },
        ...rightCategoryAgg,
      ]);
      break;
    case "tier2":
      setCategoryAgg([
        ...leftCategoryAgg,
        {
          ...categoryAgg[categoryIndex.tier0],
          tier1s: [
            ...tier1Agg.slice(0, categoryIndex.tier1),
            {
              ...tier1Agg[categoryIndex.tier1],
              tier2s: [
                ...tier1Agg[categoryIndex.tier1].tier2s.slice(0, categoryIndex.tier2),
                { ...tier1Agg[categoryIndex.tier1].tier2s[categoryIndex.tier2], tier3s: childCategories },
                ...tier1Agg[categoryIndex.tier1].tier2s.slice(categoryIndex.tier2 + 1),
              ],
            },
            ...tier1Agg.slice(categoryIndex.tier1 + 1),
          ],
        },
        ...rightCategoryAgg,
      ]);
      break;
    default:
    }
  }

  return (
    <div className={styles.category_tree}>
      <PAText fontSize={FONT_SIZE[2]} component="h2" className="hidden-xs" bold>{t("Categories")}</PAText>
      {
        categoryAgg?.map((tier0Item, t0Idx) => (
          <CategoryTreeTier0
            key={t0Idx}
            tier0Item={tier0Item}
            isActiveCategory={isActiveCategory}
            tier0={tier0}
            getPath={(item, category) => getPath(item, category, t0Idx, true)}
            setCategoryAggData={(childCategories) => setCategoryAggData("tier0", { tier0: t0Idx }, childCategories)}
            setIsCategoryTreeClick={setIsCategoryTreeClick}
          >

            {
              tier0Item.tier1s
                ?.map((item, t1Idx) => (
                  <CategoryToggler
                    className="p-l-15"
                    key={item.bucket}
                    isCategoryOpen={item.tier2s && item.tier2s.length > 0}
                    activeCategory={isActiveCategory(item.pageType, item.bucket)}
                    activeParent={tier1?.toLowerCase() === item.bucket.toLowerCase()}
                    item={item}
                    {...getPath(item, { tier1: item.bucket, t1Idx }, t0Idx, ["tier2", "tier3"].includes(currentCategoryPageType))}
                    setCategoryAggData={(childCategories) => setCategoryAggData("tier1", { tier0: t0Idx, tier1: t1Idx }, childCategories)}
                    setParallelCategoryAggData={(childCategories) => setCategoryAggData("tier0", { tier0: t0Idx, tier1: t1Idx }, childCategories)}
                    rightLineGap="calc(3.2 * var(--safari-root-font, 10px))"
                    bottomLineGap={23}
                    setIsCategoryTreeClick={setIsCategoryTreeClick}
                    tier0Url={tier0Url}
                    tier0={tier0Item.bucket}
                    isTier1
                  >
                    {
                      item.tier2s && (
                        <div>
                          {
                            item.tier2s
                              .map((tier2Item, t2Idx) => (
                                <CategoryToggler
                                  className="p-l-15"
                                  key={tier2Item.bucket}
                                  isCategoryOpen={tier2Item.tier3s && tier2Item.tier3s.length > 0}
                                  activeCategory={isActiveCategory(tier2Item.pageType, tier2Item.bucket)}
                                  activeParent={tier2?.toLowerCase() === tier2Item.bucket.toLowerCase()}
                                  item={tier2Item}
                                  {...getPath(tier2Item, {
                                    tier1: item.bucket, tier2: tier2Item.bucket, t1Idx, t2Idx,
                                  }, t0Idx, currentCategoryPageType === "tier3")}
                                  setCategoryAggData={(childCategories) => setCategoryAggData("tier2", { tier0: t0Idx, tier1: t1Idx, tier2: t2Idx }, childCategories)}
                                  rightLineGap="calc(3.2 * var(--safari-root-font, 10px))"
                                  bottomLineGap={23}
                                  setIsCategoryTreeClick={setIsCategoryTreeClick}
                                  tier0={tier0Item.bucket}
                                  tier1={item.bucket}
                                  isTier2
                                >
                                  {
                                    tier2Item.tier3s && (
                                      <div className={styles.category_t3}>
                                        {
                                          tier2Item.tier3s
                                            .map((tier3Item, t3Idx) => (
                                              <div
                                                key={tier3Item.bucket}
                                                className={classNames(styles.category_item, {
                                                })}
                                              >
                                                <CategoryItem
                                                  item={tier3Item}
                                                  category={{
                                                    tier1: item.bucket,
                                                    tier2: tier2Item.bucket,
                                                    tier3: tier3Item.bucket,
                                                    t1Idx,
                                                    t2Idx,
                                                    t3Idx,
                                                  }}
                                                  t0Idx={t0Idx}
                                                  activeCategory={isActiveCategory(tier3Item.pageType, tier3Item.bucket)}
                                                  activeParent={tier3?.toLowerCase() === tier3Item.bucket.toLowerCase()}
                                                />
                                              </div>
                                            ))
                                        }
                                      </div>
                                    )
                                  }
                                </CategoryToggler>
                              ))
                          }
                        </div>
                      )
                    }
                  </CategoryToggler>
                ))
            }
          </CategoryTreeTier0>
        ))
      }

    </div>
  );
}

function handleCategoryScroll() {
  if (isCategoryTreeClick) {
    // mobile scroll
    scrollTo("filter-action-bar", {
      duration: 500,
      delay: 100,
      smooth: true,
      offset: -110,
    });

    scrollTo("meta-info", {
      duration: 500,
      delay: 100,
      smooth: true,
      offset: -110,
    });
  }
  isCategoryTreeClick = false;
}

export default withErrorHandler(withRouteChangeComplete(CategoryTree, handleCategoryScroll));
